'use client';
import {Autocomplete as GoogleMapsAutocomplete} from '@react-google-maps/api';
import {type InputHTMLAttributes, useRef, useState} from 'react';
import useGoogleMapsAutocomplete from './UseGoogleMapsAutocomplete';

export interface AddressChangeEventType extends Address {
  addressInputStr: string;
}
interface StreetAddressInputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  apiKey: string;
  onAddressChange: (address: AddressChangeEventType | null) => void;
  fieldName: string;
  placeHolder?: string;
  value?: string;
  containerClassName?: string;
}

export interface Address {
  streetNumberValue: string;
  routeValue: string;
  zipCodeValue: string;
  placeId?: string;
}

export const StreetAddressInput = ({
  apiKey,
  onAddressChange,
  fieldName,
  placeHolder,
  value = '',
  containerClassName = '',
  style,
  ...rest
}: StreetAddressInputProps) => {
  const [streetAddress, setStreetAddress] = useState<string>(value);
  const streetAddressInput = useRef<HTMLInputElement>(null);

  const {isLoaded, options, onLoad, PlaceChange} =
    useGoogleMapsAutocomplete(apiKey);

  const onPlaceChanged = () => {
    const address = PlaceChange();
    const addressInputStr =
      (address.streetNumberValue ? `${address.streetNumberValue} ` : '') +
      address.routeValue;
    if (!addressInputStr) {
      onAddressChange(null);
      return;
    }
    if (streetAddressInput.current) {
      setStreetAddress(addressInputStr);
    }
    onAddressChange({
      ...address,
      addressInputStr,
    });
  };

  if (!isLoaded) {
    return (
      <input
        {...rest}
        style={style as React.CSSProperties}
        type="text"
        disabled
        placeholder={placeHolder}
      />
    );
  }

  return (
    <GoogleMapsAutocomplete
      onPlaceChanged={onPlaceChanged}
      onLoad={onLoad}
      options={options}
      className={containerClassName}
    >
      <input
        {...rest}
        style={style as React.CSSProperties}
        type="text"
        name={fieldName}
        placeholder={placeHolder}
        ref={streetAddressInput}
        value={streetAddress}
        onChange={e => {
          const addressInputStr = e.target.value;
          setStreetAddress(addressInputStr);
          onAddressChange({
            addressInputStr,
            streetNumberValue: '',
            routeValue: '',
            zipCodeValue: '',
          });
        }}
      />
    </GoogleMapsAutocomplete>
  );
};
